/**
 * VÉRAULT — Nylas embed brand overrides (Wave 30.4.6).
 * ════════════════════════════════════════════════════════════════
 *
 * Embed kind (verified at runtime, NOT an iframe):
 *   <nylas-scheduling>          custom element with OPEN shadow root
 *     ├── nylas-notification    (top status banner — Stencil, own shadow)
 *     ├── .select-date-page
 *     │   ├── .left-panel       — calendar half
 *     │   │   ├── .title        month/year header
 *     │   │   ├── .pagination   .chevron-left + .chevron-right
 *     │   │   ├── nylas-date-picker (own shadow)
 *     │   │   │   ├── .dates (weekday header row)
 *     │   │   │   └── .day .date  ↳ may have .current-day .selected
 *     │   │   └── .bottom  .timezone + .language dropdowns
 *     │   └── .right-panel      — time-slot half
 *     │       └── nylas-timeslot-picker (own shadow)
 *     │           └── .spots .time .button-basic  ↳ .selected when chosen
 *     └── .footer
 *         └── .powered-by-nylas
 *
 * Nylas paints every `.hydrated` Stencil host with
 * `color: rgb(49, 79, 169)` (≈ #314FA9 — their app-blue). Most SVG
 * icons use `currentColor` so cascading the right `color` value down
 * the tree handles them automatically. Backgrounds + border colors on
 * `.day.selected`, the active time-slot pill, and dropdown chrome are
 * direct properties that need their own overrides.
 *
 * Strategy:
 *   1. Set VÉRAULT palette as CSS custom properties on `:host` of the
 *      INJECTED style block. Use `:host-context([data-theme="dark"])`
 *      to flip the same vars for night mode (theme-aware).
 *   2. Override every concrete selector observed in the live DOM
 *      using those vars. !important everywhere because Nylas' own
 *      stylesheet is high-specificity Stencil-scoped CSS.
 *   3. Inject the style block into the outer shadow root AND walk all
 *      nested shadow roots (nylas-date-picker, nylas-timeslot-picker,
 *      nylas-notification, every sc-*-icon-h) so even Stencil's
 *      per-component encapsulation can't shield the app-blue.
 *
 * Not styleable with the current embed (locked outside our reach):
 *   • Form-validation error tooltips render in a top-level portal
 *     layer with the default red.
 *   • The very final "Confirmation" stencil screen shows briefly
 *     before our bookingConfirmationRedirectUrl fires and replaces
 *     the page with our own thank-you state in Schedule.jsx.
 *   • Stencil's deepest icon SVG fills that hardcode `fill="#314FA9"`
 *     instead of using `currentColor`. Empirically rare on this
 *     element set — the dump showed every icon path uses currentColor.
 *     If Nylas adds new icons that hardcode the fill in a future SDK,
 *     they would need a per-icon override added here.
 */

/**
 * The complete style block injected into every Nylas shadow root.
 *
 * Wave 30.4.7 — Aggressive baseline + Step-2 booking form coverage.
 *   • A `*` baseline `color` and `border-color` override that catches
 *     ANY view (calendar, booking form, confirmation, error state)
 *     because Nylas uses inherited `color` for nearly everything.
 *   • Specific selectors for the booking form (.event-summary,
 *     .selected-time, .input-label, .name-input, .email-input,
 *     button-component[*], .add-guest, etc.) verified against the
 *     Step-2 Nylas Stencil DOM dump.
 *   • Document-level CSS variables (defined on `:root[data-theme]`
 *     in /app/frontend/src/index.css) pierce every shadow boundary
 *     via CSS custom property inheritance — same values resolve
 *     correctly inside nested Stencil components.
 */
export const NYLAS_SHADOW_CSS = `
  /* === Override Nylas' adopted-stylesheet :host vars ============
     Each Stencil component (nylas-scheduling, nylas-timeslot-picker,
     nylas-date-picker, nylas-booking-form, ...) carries its OWN
     adopted stylesheet that sets:
        :host { --nylas-primary: #2563eb; ... }
     and every inner rule references those vars (outline-color,
     box-shadow focus rings, selected backgrounds, link colours).
     Setting our values at :host of each shadow root resolves these
     vars to the VÉRAULT palette before any inner rule evaluates.
     This is what kills the persistent blue ring on the selected
     time slot AND the blue book-now button. */
  :host {
    --nylas-primary:           #C8A85A !important;
    --nylas-primary-color:     #C8A85A !important;
    --nylas-primary-pressed:   #A88848 !important;
    --nylas-primary-bg:        transparent !important;
    --nylas-color-primary:     #C8A85A !important;
    --nylas-color-link:        #C8A85A !important;
    --nylas-success:           #C8A85A !important;
    --nylas-success-color:     #C8A85A !important;
    --nylas-info:              #C8A85A !important;
    --nylas-base-text:         #1A1A1A !important;
    --nylas-base-text-color:   #1A1A1A !important;
    --nylas-text-color:        #1A1A1A !important;
    --nylas-base-0:            transparent !important;
    --nylas-base-50:           #F5F1EB !important;
    --nylas-base-100:          #F0EBE2 !important;
    --nylas-base-200:          #E6DFD0 !important;
    --nylas-base-300:          #C8B89A !important;
    --nylas-base-400:          #A0907A !important;
    --nylas-base-500:          #6B6258 !important;
    --nylas-base-600:          #4A453F !important;
    --nylas-base-700:          #1A1A1A !important;
    --nylas-base-800:          #1A1A1A !important;
    --nylas-base-900:          #1A1A1A !important;
    --nylas-error:             #C8A85A !important;
    --nylas-error-color:       #C8A85A !important;
    --nylas-warning:           #C8A85A !important;
    --nylas-wraning:           #C8A85A !important;
  }
  :host-context([data-theme="dark"]) {
    --nylas-base-text:         #F5F1EB !important;
    --nylas-base-text-color:   #F5F1EB !important;
    --nylas-text-color:        #F5F1EB !important;
  }

  /* === BASELINE: kill ALL inherited blue everywhere ===========
     Nylas paints inherited color rgb(49,79,169) or rgb(41,48,86)
     on every Stencil host. A wildcard baseline catches anything we
     might not have named (Step 2 form, confirmation, errors). */
  *, *::before, *::after {
    color: hsl(var(--foreground)) !important;
    border-color: hsl(var(--surface-hairline) / 0.5) !important;
  }
  svg, svg * {
    fill: currentColor !important;
    stroke: currentColor !important;
  }

  /* ─── Hide branding ─────────────────────────────────────────── */
  .powered-by-nylas,
  [class*="powered-by"], [class*="poweredBy"],
  a[href*="nylas.com"], footer a[href*="nylas"],
  nylas-feedback-tracker {
    display: none !important;
    height: 0 !important; width: 0 !important;
    overflow: hidden !important; visibility: hidden !important;
  }

  /* ─── Outer surfaces: transparent so our editorial frame shows ─ */
  :host, .select-date-page, .left-panel, .right-panel,
  .booking-form, .event-summary, .booking-confirmation {
    background: transparent !important;
  }

  /* ─── Calendar header (Step 1) ─────────────────────────────── */
  .title, .current-month {
    font-family: 'Playfair Display', Georgia, serif !important;
    font-weight: 400 !important;
    letter-spacing: -0.005em !important;
    color: hsl(var(--foreground)) !important;
    opacity: 1 !important;
  }

  /* ─── Weekday labels ───────────────────────────────────────── */
  .dates > *, .nylas-date-picker .dates > * {
    color: hsl(var(--foreground-muted)) !important;
    font-family: 'JetBrains Mono', monospace !important;
    text-transform: uppercase !important;
    letter-spacing: 0.18em !important;
    font-size: 10px !important;
    opacity: 1 !important;
  }

  /* ─── Day cells ────────────────────────────────────────────── */
  .day, .date {
    color: hsl(var(--foreground)) !important;
    font-family: 'Manrope', system-ui, sans-serif !important;
    border-radius: 6px !important;
    background: transparent !important;
    opacity: 1 !important;
    transition: background-color 180ms ease, color 180ms ease !important;
  }
  .day:hover, .date:hover {
    background: hsl(var(--accent-gold) / 0.12) !important;
  }
  .day.selected, .date.selected,
  .day[aria-selected="true"], .date[aria-selected="true"] {
    background: hsl(var(--foreground)) !important;
    color: hsl(var(--background)) !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }
  .day.current-day:not(.selected), .date.current-day:not(.selected) {
    color: hsl(var(--accent-gold)) !important;
    font-weight: 600 !important;
  }
  .day[disabled], .date[disabled],
  .day.disabled, .date.disabled,
  .day[aria-disabled="true"] {
    color: hsl(var(--foreground-muted)) !important;
    opacity: 0.55 !important;
  }

  /* ─── Chevron pagination ───────────────────────────────────── */
  .chevron, .chevron-left, .chevron-right, .pagination button,
  .sc-chevron-icon-h, [class*="chevron"] {
    color: hsl(var(--foreground)) !important;
    background: transparent !important;
    border: none !important;
    opacity: 1 !important;
  }
  .chevron:hover, .pagination button:hover {
    color: hsl(var(--accent-gold)) !important;
  }

  /* ─── Time-slot pills ──────────────────────────────────────── */
  .spots, .timeslots, .time-picker-wrapper {
    background: transparent !important;
  }
  .time, .timeslots .time, .spots .time {
    border-radius: 9999px !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.6) !important;
    background: transparent !important;
    color: hsl(var(--foreground)) !important;
    font-family: 'Manrope', system-ui, sans-serif !important;
    letter-spacing: 0.04em !important;
    opacity: 1 !important;
  }
  .time:hover, .spots .time:hover {
    border-color: hsl(var(--accent-gold)) !important;
    background: hsl(var(--accent-gold) / 0.10) !important;
  }
  .time.selected, .spots .time.selected,
  .time[aria-selected="true"],
  .button-basic.selected, .time .button-basic.selected,
  .button-basic[aria-selected="true"] {
    border-color: hsl(var(--accent-gold)) !important;
    background: hsl(var(--accent-gold) / 0.14) !important;
    color: hsl(var(--foreground)) !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .time .button-basic, .time-label { color: inherit !important; background: transparent !important; border: none !important; }

  /* Kill any leftover focus/active blue rings on Stencil buttons (the
     "selected time slot" pill in Step 1 picks up a Stencil-default
     #314FA9 focus ring that bleeds blue around our gold border). */
  button, button-component, .button-basic, .time {
    outline: none !important;
    -webkit-tap-highlight-color: transparent !important;
  }
  button:focus, button:active, button:focus-visible,
  button-component:focus, button-component:active,
  .button-basic:focus, .button-basic:active, .button-basic:focus-visible,
  .time:focus, .time:active, .time:focus-visible {
    box-shadow: none !important;
    outline: none !important;
  }
  /* For focus-visible we add a quiet gold ring (already defined below)
     but kill the Stencil default everywhere it might leak. */
  *:focus { outline: none !important; }

  /* ═══ ::part overrides (CRITICAL for time slot blue ring) ═══════
     Stencil time-slot picker exposes its selected button as
     ::part(ntp__timeslot--selected) from inside its OWN shadow root.
     The Nylas <nylas-scheduling> parent then applies a blue outline +
     box-shadow via that part selector (rgb(37,99,235), 2px). Since
     ::part rules apply from outside the inner shadow, we CANNOT
     neutralise them from a style block injected inside the timeslot-
     picker's shadow — we must add ::part overrides into the outer
     nylas-scheduling shadow root, which is where this same style
     block also gets injected by our deep walker. */
  ::part(ntp__timeslot),
  ::part(ntp__timeslot--selected),
  ::part(ntp__timeslot--available),
  ::part(ntp__timeslot--unavailable),
  nylas-timeslot-picker::part(ntp__timeslot),
  nylas-timeslot-picker::part(ntp__timeslot--selected) {
    outline: none !important;
    box-shadow: none !important;
    -webkit-tap-highlight-color: transparent !important;
  }
  ::part(ntp__timeslot--selected),
  nylas-timeslot-picker::part(ntp__timeslot--selected) {
    border: 1px solid hsl(var(--accent-gold)) !important;
    background: hsl(var(--accent-gold) / 0.14) !important;
    color: hsl(var(--foreground)) !important;
  }
  /* Also kill any blue ring on the Step-2 booking form button parts. */
  ::part(nbf__button-primary),
  ::part(nbf__button-outline),
  ::part(nbf__button-ghost),
  ::part(nbf__input-textfield),
  ::part(nbf__input-wrapper),
  ::part(nbf__dropdown),
  ::part(nbf__dropdown-button),
  nylas-booking-form::part(nbf__button-primary),
  nylas-booking-form::part(nbf__button-outline),
  nylas-booking-form::part(nbf__input-textfield) {
    outline: none !important;
    box-shadow: none !important;
  }
  ::part(nbf__button-primary),
  nylas-booking-form::part(nbf__button-primary) {
    background: hsl(var(--foreground)) !important;
    color: hsl(var(--background)) !important;
    border: 1px solid hsl(var(--foreground)) !important;
  }
  ::part(nbf__button-outline),
  ::part(nbf__button-ghost),
  nylas-booking-form::part(nbf__button-outline),
  nylas-booking-form::part(nbf__button-ghost) {
    background: transparent !important;
    color: hsl(var(--foreground)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.6) !important;
  }
  ::part(nbf__input-textfield),
  nylas-booking-form::part(nbf__input-textfield) {
    background: hsl(var(--background)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.6) !important;
    color: hsl(var(--foreground)) !important;
  }

  /* ═══ STEP 2: BOOKING FORM ═══════════════════════════════════
     New view loaded after picking a date+time. Has its own Stencil
     components (button-component, nylas-form-input, etc.). */

  /* The summary block (date + time + timezone) */
  .event-summary, .selected-event, .summary,
  .event-summary > *, .selected-event > * {
    color: hsl(var(--foreground)) !important;
  }
  .event-summary .title, .event-summary [class*="title"],
  .selected-date, .selected-time, .selected-timezone,
  .booking-summary-title, .booking-summary-date {
    color: hsl(var(--foreground)) !important;
    font-family: 'Playfair Display', Georgia, serif !important;
    font-weight: 400 !important;
  }
  .selected-time, [class*="time-display"], [class*="time-range"] {
    font-family: 'Manrope', system-ui, sans-serif !important;
    color: hsl(var(--foreground)) !important;
  }
  .timezone-display, .selected-timezone, [class*="timezone"] {
    color: hsl(var(--foreground-muted)) !important;
    font-family: 'JetBrains Mono', monospace !important;
    text-transform: uppercase !important;
    letter-spacing: 0.16em !important;
    font-size: 11px !important;
  }

  /* Form inputs */
  input, textarea, select,
  input[type="text"], input[type="email"], input[type="tel"] {
    background: hsl(var(--background)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.6) !important;
    border-radius: 6px !important;
    font-family: 'Manrope', system-ui, sans-serif !important;
    color: hsl(var(--foreground)) !important;
    padding: 0.65rem 0.9rem !important;
  }
  input::placeholder, textarea::placeholder {
    color: hsl(var(--foreground-muted)) !important;
    opacity: 0.7 !important;
  }
  input:focus, textarea:focus, select:focus {
    border-color: hsl(var(--accent-gold)) !important;
    box-shadow: 0 0 0 2px hsl(var(--accent-gold) / 0.30) !important;
    outline: none !important;
  }

  /* Labels and required asterisks */
  label, .input-label, [class*="label"] {
    color: hsl(var(--foreground-muted)) !important;
    font-family: 'JetBrains Mono', monospace !important;
    text-transform: uppercase !important;
    letter-spacing: 0.18em !important;
    font-size: 10px !important;
    font-weight: 500 !important;
  }
  .required, [class*="required"], .asterisk { color: hsl(var(--accent-gold)) !important; }

  /* "Add guest" link */
  .add-guest, [class*="add-guest"], button.add-guest {
    color: hsl(var(--foreground-muted)) !important;
    background: transparent !important;
    border: none !important;
    font-family: 'JetBrains Mono', monospace !important;
    text-transform: uppercase !important;
    letter-spacing: 0.16em !important;
    font-size: 11px !important;
  }
  .add-guest:hover { color: hsl(var(--accent-gold)) !important; }
  .add-guest svg, .add-guest [class*="icon"] { color: currentColor !important; }

  /* ═══ ALL BUTTONS — kill the blue Book Now + Back ═══════════
     This is the most aggressive selector group. We match every
     button by tag, role, and Stencil host so the blue cannot leak. */
  button,
  button-component, button-component button, button-component .button-basic,
  .button-basic, .btn, [role="button"]:not(.day):not(.date):not(.time),
  button[type="submit"], button[type="button"],
  button[class*="book"], button[class*="submit"], button[class*="confirm"],
  button[class*="back"], button[class*="cancel"], button[class*="next"],
  button[class*="primary"], button[class*="secondary"] {
    font-family: 'Manrope', system-ui, sans-serif !important;
    font-weight: 500 !important;
    letter-spacing: 0.01em !important;
    border-radius: 9999px !important;
    transition: background-color 180ms ease, border-color 180ms ease !important;
  }

  /* PRIMARY buttons (Book now, Confirm, Continue) — charcoal pill */
  button[type="submit"],
  button-component[primary] button, button-component[variant="primary"] button,
  button[class*="book-now"], button[class*="bookNow"],
  button[class*="confirm"], button[class*="primary"],
  .bottom button.button-basic, .footer button.button-basic,
  .book-button, .submit-button,
  /* Wave 30.4.8 — the actual Step-2 primary button class is
     button-primary (singular, inside button-component). The outer
     custom element is part="nbf__button-primary" and gets the
     charcoal background; the inner native button must have INVERTED
     text colour or the label is invisible on the dark pill. */
  button.button-primary,
  button-component[part~="nbf__button-primary"],
  button-component[part~="nbf__button-primary"] button,
  button-component[part~="nbf__button-primary"] .button-primary,
  button-component[part~="nbf__button-primary"] *,
  [part~="nbf__button-primary"],
  [part~="nbf__button-primary"] * {
    background: hsl(var(--foreground)) !important;
    color: hsl(var(--background)) !important;
    border: 1px solid hsl(var(--foreground)) !important;
    padding: 0.85rem 1.75rem !important;
    font-size: 13px !important;
  }
  /* Inner <button> can be transparent so the host's charcoal shows
     through — but the LABEL must be bone, not charcoal. */
  button-component[part~="nbf__button-primary"] button.button-primary,
  [part~="nbf__button-primary"] button.button-primary {
    background: transparent !important;
    color: hsl(var(--background)) !important;
    border: none !important;
    padding: 0 !important;
  }
  button[type="submit"]:hover,
  button-component[primary] button:hover,
  button[class*="book-now"]:hover, button[class*="confirm"]:hover {
    background: hsl(var(--foreground) / 0.9) !important;
    border-color: hsl(var(--foreground) / 0.9) !important;
  }

  /* SECONDARY buttons (Back) — hairline outline */
  button[type="button"]:not([class*="book"]):not([class*="confirm"]):not([class*="primary"]),
  button-component[variant="secondary"] button, button-component:not([primary]) button,
  button[class*="back"], button[class*="cancel"], button[class*="secondary"],
  .back-button, .cancel-button {
    background: transparent !important;
    color: hsl(var(--foreground)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.6) !important;
    padding: 0.85rem 1.75rem !important;
    font-size: 13px !important;
  }
  button[class*="back"]:hover, .back-button:hover {
    border-color: hsl(var(--accent-gold)) !important;
    color: hsl(var(--foreground)) !important;
  }

  /* Bottom row: timezone + language */
  .bottom { background: transparent !important; }
  .timezone, .language, nylas-locale-switch, .select-wrapper {
    color: hsl(var(--foreground-muted)) !important;
    background: transparent !important;
  }
  .dropbtn, .timezone .dropbtn, .language .dropbtn {
    background: transparent !important;
    color: hsl(var(--foreground-muted)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.5) !important;
    border-radius: 6px !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 10.5px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.16em !important;
    padding: 6px 10px !important;
  }
  .dropbtn:hover { color: hsl(var(--foreground)) !important; border-color: hsl(var(--accent-gold) / 0.5) !important; }
  .dropdown {
    background: hsl(var(--background-paper)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.5) !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08) !important;
  }
  .selected-option { color: hsl(var(--accent-gold)) !important; }

  /* Right-panel selected-day header */
  .right-panel .header, .right-panel h2, .right-panel h3,
  .selected-date, .selected-date-label {
    color: hsl(var(--foreground)) !important;
    opacity: 1 !important;
    font-family: 'Playfair Display', Georgia, serif !important;
    font-weight: 400 !important;
  }

  /* Focus rings — quiet gold */
  *:focus-visible {
    outline: 1px solid hsl(var(--accent-gold)) !important;
    outline-offset: 2px !important;
    box-shadow: none !important;
  }

  /* Tooltips */
  .tooltip-container, .tooltip-content {
    background: hsl(var(--background-paper)) !important;
    color: hsl(var(--foreground)) !important;
    border: 1px solid hsl(var(--surface-hairline) / 0.5) !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
    font-family: 'Manrope', system-ui, sans-serif !important;
  }
  .info-icon, .sc-info-icon-h { color: hsl(var(--foreground-muted)) !important; }

  /* Top notification banner */
  nylas-notification, nylas-notification * {
    background: transparent !important;
    color: hsl(var(--foreground)) !important;
    border-color: hsl(var(--surface-hairline) / 0.5) !important;
  }

  /* Kill saas-y elevation, divider rules in left/right gap */
  [class*="card"], [class*="panel"], [class*="container"] { box-shadow: none !important; }
  .left-panel { border-right: 1px solid hsl(var(--surface-hairline) / 0.4) !important; }
  @media (max-width: 768px) {
    .left-panel { border-right: 0 !important; border-bottom: 1px solid hsl(var(--surface-hairline) / 0.4) !important; }
  }

  /* ─── Calendar icon (Stencil sc-calendar-icon-h on Step 2) ─── */
  .sc-calendar-icon-h, [class*="calendar-icon"], [class*="globe-icon"], [class*="clock-icon"] {
    color: hsl(var(--foreground-muted)) !important;
  }
`;

/**
 * Inject NYLAS_SHADOW_CSS into the outer shadow root + every nested
 * Stencil shadow root we can find. Returns a teardown function.
 *
 * Wave 30.4.7 — Interval-based deep walker.
 *
 * Why not MutationObserver alone?
 * Shadow roots ATTACHING (via `element.attachShadow()`) DO NOT fire
 * any DOM mutation event. So if Stencil hydrates a child component
 * lazily (e.g., the Step-2 booking form is built only after the user
 * picks a date+time), MutationObserver never sees it. Step 2 was
 * therefore being rendered with Nylas' raw blue palette.
 *
 * The reliable approach is to poll. We re-walk every 400ms for 30s
 * after first mount — covers all Stencil hydration windows we've
 * observed. CPU cost is negligible (microseconds per walk) and after
 * 30s the calendar tree is fully settled. Each shadow root gets its
 * own copy of the stylesheet exactly once (WeakSet de-dup), so the
 * polling is idempotent.
 */
export function injectIntoNylasShadow(el) {
  if (!el) return () => {};
  let cancelled = false;
  const injectedRoots = new WeakSet();
  let pollTimer = null;
  let stopTimer = null;
  let observer = null;

  const inject = (root) => {
    if (cancelled || !root) return;
    try {
      // Always refresh textContent if a prior style block exists — Stencil's
      // shadow root persists across React re-mounts, so a stale CSS body
      // would otherwise stick around forever. We keep ONE style block per
      // root and keep its textContent in sync with the latest module CSS.
      const existing = root.querySelector && root.querySelector("style[data-vt-nylas-override]");
      if (existing) {
        if (existing.textContent !== NYLAS_SHADOW_CSS) {
          existing.textContent = NYLAS_SHADOW_CSS;
        }
        injectedRoots.add(root);
        return;
      }
      if (injectedRoots.has(root)) return;
      const style = document.createElement("style");
      style.setAttribute("data-vt-nylas-override", "true");
      style.textContent = NYLAS_SHADOW_CSS;
      root.appendChild(style);
      injectedRoots.add(root);
    } catch {
      /* noop */
    }
  };

  const walkAndInject = (root) => {
    if (!root || cancelled) return;
    inject(root);
    try {
      const all = root.querySelectorAll("*");
      for (const node of all) {
        if (node.shadowRoot) walkAndInject(node.shadowRoot);
      }
    } catch {
      /* noop */
    }
  };

  const walkFromHost = () => {
    if (cancelled || !el.shadowRoot) return;
    walkAndInject(el.shadowRoot);
  };

  // Phase 1: kick off polling as soon as the outer shadow root exists.
  const start = () => {
    if (cancelled) return;
    if (!el.shadowRoot) {
      window.setTimeout(start, 80);
      return;
    }
    // Immediate first walk
    walkFromHost();
    // Then poll every 400ms for 30 seconds — covers all lazy Stencil
    // hydration cycles (calendar → time picker → booking form → submit).
    pollTimer = window.setInterval(walkFromHost, 400);
    stopTimer = window.setTimeout(() => {
      if (pollTimer) window.clearInterval(pollTimer);
      pollTimer = null;
      // After 30s, do ONE final walk so any super-late attach gets caught.
      walkFromHost();
    }, 30000);

    // Also keep a MutationObserver as a belt-and-suspenders catch for
    // visible DOM changes inside the outer shadow root (it won't catch
    // shadow-attach events, but it catches new descendants under known
    // hosts so we re-walk on user nav within the calendar).
    try {
      observer = new MutationObserver(walkFromHost);
      observer.observe(el.shadowRoot, { childList: true, subtree: true });
    } catch {
      /* noop */
    }
  };

  start();

  return () => {
    cancelled = true;
    if (pollTimer) window.clearInterval(pollTimer);
    if (stopTimer) window.clearTimeout(stopTimer);
    if (observer) observer.disconnect();
  };
}

/* Backwards-compat / Wave 30.4.6 — Nylas internally uses
   `--nylas-base-text` (and a few siblings) to drive its text/icon
   colour, including in dark mode where it resolves to #314fa9 (their
   blue). Setting these on the OUTER element STYLE attribute cascades
   through the shadow boundary and overrides Nylas' own defaults
   before their dark-mode stylesheet ever evaluates. */
export const NYLAS_HOST_CSS_VARS = `
  --nylas-base-text:        #1A1A1A;
  --nylas-base-text-color:  #1A1A1A;
  --nylas-text-color:       #1A1A1A;
  --nylas-primary:          #C8A85A;
  --nylas-primary-color:    #C8A85A;
  --nylas-primary-bg:       transparent;
  --nylas-color-primary:    #C8A85A;
  --nylas-success-color:    #C8A85A;
  --nylas-color-link:       #C8A85A;
  color-scheme: light;
`;

/* Wave 30.4.6 — The same overrides for night mode. Applied by
   NylasScheduler.jsx when `data-theme="dark"` is active on the
   document. Critically uses `color-scheme: dark` so the browser's
   native form controls inside the embed (date inputs, native
   selects) also pick up the dark palette. */
export const NYLAS_HOST_CSS_VARS_DARK = `
  --nylas-base-text:        #F5F1EB;
  --nylas-base-text-color:  #F5F1EB;
  --nylas-text-color:       #F5F1EB;
  --nylas-primary:          #C8A85A;
  --nylas-primary-color:    #C8A85A;
  --nylas-primary-bg:       transparent;
  --nylas-color-primary:    #C8A85A;
  --nylas-success-color:    #C8A85A;
  --nylas-color-link:       #C8A85A;
  color-scheme: dark;
`;
